/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm, Sorting, StandardLoadOptions, SortDirection } from 'app/shared/lib/essette-site';
import { validate } from 'app/shared/elements/ess-rule';
import { apiCall } from 'app/shared/lib/api/api';
import { I18N } from 'aurelia-i18n';
import { bindable } from 'aurelia-typed-observable-plugin';
import { modelCodeToResourceKey } from 'app/shared/lib/i18n';
import { IContactLogConfig } from 'app/core/models/essette-interfaces';
import { ContactLogService } from 'app/core/services/contact-log-service';
import { MemberService } from 'app/cd/services/member-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as UM from 'app/um/models/essette-api';
import { SummaryLoader } from 'app/cross/services/summary-loader';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';

const csCallCode: string = 'CS.Call';

@autoinject()
export class ContactLogGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView })
    public targetId: string | null = null;

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;
    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public member: CD.Member | null = null;

    protected methods: ReadonlyArray<CM.CaseContactLogMethod | UM.AuthContactLogMethod> = [];
    protected contacts: ReadonlyArray<CD.MemberContactComposite> = [];

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public contactLogConfig: IContactLogConfig = {};

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = true;

    protected validateForm = validate;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public showContactLinks: boolean = true;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public pageSize: number = 10;
    protected openProviderSummary = openProviderSummary;
    protected showProviderSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;
    protected showMemberSummaryLink: boolean = false;
    protected modelCodeToResourceKey = modelCodeToResourceKey;
    protected contactLogs: Core.ContactLog[] = [];
    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected hasCaseRoute = false;
    protected hasAuthRoute = false;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    private $saveContactLogModal!: EssModalCustomElement;
    protected $saveContactLogForm!: IForm;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly contactLogService: ContactLogService,
        private readonly memberService: MemberService,
        private readonly i18n: I18N,
        private readonly summaryLoader: SummaryLoader,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'contacted.dateTimeRange.start',
                direction: SortDirection.Descending
            }
        ];
    }

    protected bind(): void {
        this.checkPermissions();
        this.contactLogService.configure(this.contactLogConfig);
        this.reload();
    }


    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
    }

    public async reload(): Promise<void> {
        if(this.contactLogConfig.rollupConfig) {
            this.loadContactLogByMember();
        } else {
            this.loadContactLogByTarget();
        }
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected parseDisplayString(contact: string, i18nReference?: string): string {
        const re = /\(\S*\)$/
        const index = contact.search(re);
        let contactToReturn = contact;
        if(index > 0) {
            contactToReturn = contact.substring(0, index - 1);
        }
        return i18nReference ? `${contactToReturn} (${this.i18n.tr(i18nReference)})` : contactToReturn;
    }

    /** Opens the respective summary */
    protected openSummary(code: string, id: string): void {
        this.summaryLoader.openAttachedToSummary(code, id, this.hasCaseRoute, this.hasAuthRoute);
    }

    /** Opens the call summary */
    protected openCallSummary(id: string): void {
        this.summaryLoader.openAttachedToSummary(csCallCode, id, this.hasCaseRoute, this.hasAuthRoute);
    }

    /** Returns whether or not a user has permission to open a summary */
    protected showLink(code: string): boolean {
        return this.summaryLoader.hasPermission(this.accountContext, code);
    }

    /** Returns whether or not a user has permission to open the call summary */
    protected showCallLink(): boolean {
        return this.showLink(csCallCode);
    }

    /** Returns an i18n tooltip string */
    protected attachedToToolTip(code: string): string {
        return this.summaryLoader.attachedToToolTip(code);
    }

    //** Returns a codes i18n description string */
    protected attachedToType(code: string): string {
        return this.summaryLoader.attachedToType(code);
    }

    protected closeSaveContactLogModal(): void {
        if(this.$saveContactLogForm.reset) {
            this.$saveContactLogForm.reset();
        }
        this.$saveContactLogModal.close();
    }

    protected async handleSaveContactLogFormSuccess(): Promise<void> {
        if(this.$saveContactLogForm.reset) {
            this.$saveContactLogForm.reset();
        }
        this.$saveContactLogModal.close();
        await this.reload();
    }

    protected async saveContactLog(): Promise<void> {
        await this.loadLookups();
        this.$saveContactLogModal.open();
    }



    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadContactLogByMember(): Promise<void> {
        // prioritize member.id over targetId
        const memberId = this.member ? this.member.id : this.memberId;
        if(!memberId) return;

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: false
        };

        const includes: string[] = [
            'method',
            'outcome',
            'contacts',
            'attachedTo',
            'contacted.byUser',
        ];

        const fields: string[] = [
            'attachedTo.type.code',
            'attachedTo.description',
            'contacted.dateTimeRange.start',
            'contacted.byUser.description',
            'contacts.type.code',
            'contacts.description',
            'method.description',
            'number',
            'outcome.description',
            'summary',
            'targetType.code'
        ];

        try {
            this.state.beginLoad();

            let response = await this.contactLogService.loadContactLogByMember(memberId, options, includes, fields);
            this.contactLogs = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadContactLogByTarget(): Promise<void> {
        if(!this.targetId) return;

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: false
        };

        const includes: string[] = [
            'method',
            'outcome',
            'contacts',
            'contacted.byUser'
        ];

        const fields: string[] = [
            'contacted.dateTimeRange.start',
            'contacted.byUser.description',
            'contacts.type.code',
            'contacts.description',
            'method.description',
            'outcome.description',
            'summary'
        ];

        try {
            this.state.beginLoad();
            let response = await this.contactLogService.loadContactLogByTarget(this.targetId, options, includes, fields);
            this.contactLogs = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        if(!this.member?.id) return;

        try {
            this.state.beginLoad();

            const methodFields = [
                'description',
                'hasOutcomes',
                'outcomes.description'
            ];

            const methodIncludes = [
                'outcomes'
            ];

            const contactFields = [
                'description',
                'memberProvider.provider.id',
                'memberContact.relatedMember.name.full'
            ];

            const contactIncludes: string[] = [
                'memberContact',
                'memberContact.relatedMember',
                'memberContact.relationship',
                'memberProvider',
                'memberProvider.relationship'
            ];

            const [methods, contacts] = await Promise.all(
                [
                    this.contactLogService.loadContactLogMethods({ all: true }, methodIncludes, false, methodFields),
                    this.memberService.loadMemberContactsComposite(this.member.id, { all: true }, contactIncludes, contactFields)
                ]);

            // Include the current member themselves as one of the contact options.
            const selfContact = {
                memberContact: {
                    id: this.member.id,
                    relatedMember: {
                        id: this.member.id,
                        name: this.member.name
                    }
                },
                $display: this.member.name?.full + ` (${this.i18n.tr('cd:member.$')})`
            };

            const allContacts = [selfContact, ...contacts.results];
            allContacts.sort((a, b) => {
                const nameA = a.$display || a.memberContact?.relatedMember?.name?.full || '';
                const nameB = b.$display || b.memberContact?.relatedMember?.name?.full || '';
                return nameA.localeCompare(nameB);
            });

            this.contacts = allContacts;
            this.methods = methods.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
